import PropTypes from 'prop-types';
import { useMemo, useState, useCallback, useEffect } from 'react';
// @mui
import { CssBaseline } from '@mui/material';
import { ThemeProvider as MUIThemeProvider, createTheme, StyledEngineProvider } from '@mui/material/styles';
//
import palette from './palette';
import shadows from './shadows';
import typography from './typography';
import GlobalStyles from './globalStyles';
import customShadows from './customShadows';
import componentsOverride from './overrides';
import ThemeContext from './ThemeContext';

// ----------------------------------------------------------------------

ThemeProvider.propTypes = {
  children: PropTypes.node,
};

export default function ThemeProvider({ children }) {
  const [themeMode, setThemeModeState] = useState(() => {
    try {
      return localStorage.getItem('mtc_theme_mode') || 'light';
    } catch {
      return 'light';
    }
  });

  const toggleTheme = useCallback(() => {
    setThemeModeState((prev) => {
      const next = prev === 'light' ? 'dark' : 'light';
      try {
        localStorage.setItem('mtc_theme_mode', next);
      } catch {}
      return next;
    });
  }, []);

  const restoreTheme = useCallback(() => {
    setThemeModeState('light');
    try {
      localStorage.setItem('mtc_theme_mode', 'light');
    } catch {}
  }, []);

  const setThemeMode = useCallback((mode) => {
    const next = mode === 'dark' ? 'dark' : 'light';
    setThemeModeState(next);
    try {
      localStorage.setItem('mtc_theme_mode', next);
    } catch {}
  }, []);

  // Listen to external theme changes across tabs or windows
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === 'mtc_theme_mode' && e.newValue) {
        setThemeModeState(e.newValue);
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const themeOptions = useMemo(
    () => ({
      palette: palette(themeMode),
      shape: { borderRadius: 8 },
      typography,
      shadows: shadows(themeMode),
      customShadows: customShadows(themeMode),
    }),
    [themeMode]
  );

  const theme = createTheme(themeOptions);
  theme.components = componentsOverride(theme);

  const contextValue = useMemo(
    () => ({
      themeMode,
      isDark: themeMode === 'dark',
      toggleTheme,
      restoreTheme,
      setThemeMode,
    }),
    [themeMode, toggleTheme, restoreTheme, setThemeMode]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      <StyledEngineProvider injectFirst>
        <MUIThemeProvider theme={theme}>
          <CssBaseline />
          <GlobalStyles />
          {children}
        </MUIThemeProvider>
      </StyledEngineProvider>
    </ThemeContext.Provider>
  );
}
